import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Dialog, DialogContent, DialogTitle, Link, Tooltip } from '@mui/material'
import dayjs from 'dayjs'

import { apiZodios } from '~api/apiZodios'
import {
  Chip,
  EmptyState,
  Icon,
  IconButton,
  Table,
  type TableHeader,
  Typography,
} from '~components/internal'
import { formatPhoneNumber } from '~components/internal/formatPhoneNumber'
import { type ActiveCall, type WarmHandoffData } from '~types'

interface ActiveCallsDialogProps {
  open: boolean
  onClose: () => void
  organizationIdentityId: string
  userId: string | null
  updateToken: number
}

type ActiveCallRow = ActiveCall & { id: string }

const REFRESH_INTERVAL_MS = 30000
// Shared by both tables so the Active and Recent columns line up.
const COLUMN_WIDTH = 200

const formatElapsed = (timestamp: string, now: number): string => {
  const minutes = Math.max(0, Math.floor((now - Date.parse(timestamp)) / 60000))
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m ago`
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m ago`
}

const toRows = (calls: ActiveCall[]): ActiveCallRow[] =>
  calls.map((call) => ({ ...call, id: call.ai_interaction_id }))

const DetailField: React.FC<{
  label: string
  value?: React.ReactNode
  className?: string
}> = ({ label, value, className }) => (
  <Box className={className}>
    <Typography variant='caption' color='text.secondary'>
      {label}
    </Typography>
    <Box component='div'>
      {typeof value === 'string' || !value ? (
        <Typography variant='body2'>{value || '—'}</Typography>
      ) : (
        value
      )}
    </Box>
  </Box>
)

const ChipList: React.FC<{ values?: string[]; color?: 'blue' }> = ({ values, color }) =>
  values && values.length > 0 ? (
    <Box className='active-calls-dialog__chips'>
      {values.map((value) => (
        <Chip key={value} size='small' color={color} label={value} />
      ))}
    </Box>
  ) : null

const TransferDetails: React.FC<{
  call: ActiveCall
  handoff: WarmHandoffData
  onOpenContact: (path: string) => void
}> = ({ call, handoff, onOpenContact }) => {
  const contactPath = call.contact_record
    ? `/contacts/${call.contact_record.contact_book_code}/${call.contact_record.handle}`
    : null
  const callerName = handoff.contact.name || call.contact_record?.name

  return (
    <Box className='active-calls-dialog__transfer'>
      <Icon
        name='subdirectoryArrowRight'
        fontSize='small'
        className='active-calls-dialog__transfer-arrow'
        aria-hidden
      />
      <Box className='active-calls-dialog__transfer-card'>
        <Box className='active-calls-dialog__transfer-header'>
          <Typography variant='subtitle2' className='active-calls-dialog__transfer-title'>
            {handoff.transfer_number
              ? `Transferred to ${formatPhoneNumber(handoff.transfer_number)}`
              : 'Transferred'}
          </Typography>
          {handoff.status === 'pending' && <Chip size='small' color='orange' label='Summarizing' />}
          {handoff.status === 'failed' && (
            <Chip size='small' color='red' label='Summary unavailable' />
          )}
        </Box>
        <Box className='active-calls-dialog__transfer-grid'>
          <DetailField
            label='Caller'
            value={
              contactPath ? (
                <Link
                  component='button'
                  variant='body2'
                  underline='hover'
                  onClick={() => onOpenContact(contactPath)}
                >
                  {callerName || 'View contact'}
                </Link>
              ) : callerName ? (
                callerName
              ) : (
                <Typography variant='body2' className='active-calls-dialog__not-provided'>
                  Not provided
                </Typography>
              )
            }
          />
          <DetailField label='Reason for transfer' value={handoff.reason_for_transfer} />
          <DetailField
            label='Key topics'
            value={<ChipList values={handoff.key_topics} color='blue' />}
          />
        </Box>
        {handoff.summary && (
          <DetailField
            label='Summary'
            value={handoff.summary}
            className='active-calls-dialog__transfer-summary'
          />
        )}
      </Box>
    </Box>
  )
}

const ActiveCallsDialog: React.FC<ActiveCallsDialogProps> = ({
  open,
  onClose,
  organizationIdentityId,
  userId,
  updateToken,
}) => {
  const navigate = useNavigate()
  const [activeCalls, setActiveCalls] = useState<ActiveCallRow[]>([])
  const [recentCalls, setRecentCalls] = useState<ActiveCallRow[]>([])
  const [loading, setLoading] = useState(false)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [now, setNow] = useState(Date.now())
  const [refreshToken, setRefreshToken] = useState(0)

  useEffect(() => {
    if (!open || !userId) return undefined
    let isCurrent = true
    setLoading(true)
    apiZodios
      .ai_listActiveCalls({ user_id: userId, organization_identity_id: organizationIdentityId })
      .then((response) => {
        if (!isCurrent) return
        setActiveCalls(toRows(response.active_calls))
        setRecentCalls(toRows(response.recent_calls))
        setHasLoaded(true)
        setNow(Date.now())
      })
      .catch((err) => console.error('Failed to load active calls:', err))
      .finally(() => {
        if (isCurrent) setLoading(false)
      })
    return (): void => {
      isCurrent = false
    }
  }, [open, userId, organizationIdentityId, updateToken, refreshToken])

  // Recent calls age out of their two-minute window, so refresh while open.
  useEffect(() => {
    if (!open) return undefined
    const interval = window.setInterval(
      () => setRefreshToken((token) => token + 1),
      REFRESH_INTERVAL_MS,
    )
    return (): void => window.clearInterval(interval)
  }, [open])

  const handleOpenContact = (path: string): void => {
    onClose()
    navigate(path)
  }

  const fromHeader: TableHeader<ActiveCallRow> = {
    id: 'from',
    label: 'From',
    width: COLUMN_WIDTH,
    render: (row) => row.contact_record?.name || formatPhoneNumber(row.from_phone_number),
  }
  const toHeader: TableHeader<ActiveCallRow> = {
    id: 'to',
    label: 'To',
    width: COLUMN_WIDTH,
    render: (row) => formatPhoneNumber(row.to_phone_number),
  }
  const startedHeader: TableHeader<ActiveCallRow> = {
    id: 'started',
    label: 'Started',
    width: COLUMN_WIDTH,
    render: (row) => (
      <Tooltip title={dayjs(row.started).format('MMM D, YYYY h:mm:ss A')}>
        <span>
          {dayjs(row.started).format('h:mm A')} · {formatElapsed(row.started, now)}
        </span>
      </Tooltip>
    ),
  }

  const activeHeaders: TableHeader<ActiveCallRow>[] = [
    fromHeader,
    toHeader,
    startedHeader,
    {
      id: 'status',
      label: 'Status',
      render: () => <Chip size='small' color='green' label='In progress' />,
    },
  ]

  const recentHeaders: TableHeader<ActiveCallRow>[] = [
    fromHeader,
    toHeader,
    startedHeader,
    {
      id: 'status',
      label: 'Status',
      render: (row) =>
        row.warm_handoff ? (
          <Chip size='small' color='blue' label='Transferred' />
        ) : (
          <Chip
            size='small'
            label={row.ended ? `Ended ${formatElapsed(row.ended, now)}` : 'Ended'}
          />
        ),
    },
  ]

  const isInitialLoad = loading && !hasLoaded

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='md'>
      <DialogTitle className='active-calls-dialog__title'>
        Active Voice Calls
        <Tooltip title='Close'>
          <IconButton size='small' aria-label='Close' onClick={onClose}>
            <Icon name='close' fontSize='small' />
          </IconButton>
        </Tooltip>
      </DialogTitle>
      <DialogContent className='active-calls-dialog__content'>
        <Box className='active-calls-dialog__section'>
          <Typography variant='subtitle2'>Active Calls</Typography>
          {!isInitialLoad && activeCalls.length === 0 ? (
            <EmptyState
              variant='micro'
              iconName='call'
              title='No active calls'
              description='Calls handled by your AI phone lines appear here while they are in progress.'
            />
          ) : (
            <Box className='active-calls-dialog__table'>
              <Table
                headers={activeHeaders}
                data={activeCalls}
                size='small'
                fixedLayout
                loading={isInitialLoad}
                ariaLabel='Active calls'
              />
            </Box>
          )}
        </Box>
        <Box className='active-calls-dialog__section'>
          <Typography variant='subtitle2'>Recent Calls</Typography>
          {!isInitialLoad && recentCalls.length === 0 ? (
            <Typography variant='body2' color='text.secondary'>
              Calls that ended or were transferred in the last two minutes appear here.
            </Typography>
          ) : (
            <Box className='active-calls-dialog__table'>
              <Table
                headers={recentHeaders}
                data={recentCalls}
                size='small'
                fixedLayout
                loading={isInitialLoad}
                ariaLabel='Recent calls'
                renderRowDetail={(row) =>
                  row.warm_handoff ? (
                    <TransferDetails
                      call={row}
                      handoff={row.warm_handoff}
                      onOpenContact={handleOpenContact}
                    />
                  ) : null
                }
              />
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default ActiveCallsDialog
